// lib/proto/diagnostic-examples.ts — W1 evidence sprint #3: three worked
// diagnostic examples for /brand-engineering (V3-MASTER-SPEC §7 W1).
//
// HONESTY POLICY (天條 — unverified-registry): these are DEMONSTRATION runs on
// sample copy written for illustration. They are NOT client data and must
// always render with the `sampleNote` disclaimer. Output format follows the
// /diagnose comparative-scoring spec (§2.6): 清晰度｜差異化｜證據力 ＋ 品牌張力句.
// The 同業分佈 (peer-distribution) axis ships with the real AI instrument in
// Phase 2 — these static examples show scores only, no invented percentiles.

import type { Bi } from "./content"

export type DiagnosticExample = {
  id: string
  label: Bi // what kind of copy this sample represents
  input: string // the sample text that was diagnosed (zh)
  scores: {
    clarity: number // 清晰度 0–100
    differentiation: number // 差異化 0–100
    evidence: number // 證據力 0–100
  }
  findings: Bi // what the pattern-match caught
  tensionLine: Bi // 品牌張力句 — the one generated field (§2.6)
}

// B7 (v3, 2026-07-11)：「系統輸出的格式示意」拗口——微修
export const sampleNote = {
  zh: "以下是三個示範診斷，樣本文字為說明而寫，非真實客戶資料。分數只示意輸出格式；真正的診斷，用你自己的文字跑一次。",
  en: "Three demonstration runs on sample copy written for illustration, not client data. The real diagnostic runs on your own words.",
} as const

export const diagnosticExamples: DiagnosticExample[] = [
  {
    id: "component-maker",
    label: { zh: "深科技零組件廠的公司介紹", en: "A deep-tech component maker's About page" },
    input:
      "我們是領先業界的智慧解決方案供應商，以創新技術與世界級品質，提供端到端的次世代產品，賦能客戶的數位轉型。",
    scores: { clarity: 31, differentiation: 18, evidence: 12 },
    findings: {
      // B5 (v3, P1)：修正事實錯誤——樣本實為 48 字非 34 字；「不虛構數字」
      // 是天條，拿掉字數最穩（樣本日後改字不會再錯）。
      zh: "一句話裡塞了六個買家無法驗證的形容詞（領先、創新、世界級、端到端、次世代、賦能），沒有一個名詞說出你實際做什麼。這是複雜性陷阱的典型型態：用行話省略決策。",
      en: "Six unverifiable adjectives in one sentence, zero nouns that say what you actually make. Jargon as a substitute for deciding.",
    },
    tensionLine: {
      zh: "你的技術是真的，但市場在這段話裡找不到在乎的理由。",
      en: "Your technology is real. This paragraph gives the market no reason to care.",
    },
  },
  {
    id: "pre-ipo",
    label: { zh: "準上市公司的投資人頁", en: "A pre-IPO company's investor page" },
    input:
      "本公司深耕產業三十年，營收穩定成長，技術獲多項專利，未來將持續強化研發能量，追求股東最大利益。",
    scores: { clarity: 55, differentiation: 27, evidence: 41 },
    findings: {
      // B4 (v3)：「連接」→「連結」（zh-TW），全段修節奏
      zh: "有年資、有專利，證據的原料都在，卻沒有被組織成投資人能讀的敘事：三十年被寫成「老」，不是「驗證過」；專利數量沒有連上定價權或護城河。掛牌前的信任判決，靠的就是這種連結。",
      en: "The raw evidence exists (thirty years, patents), but it isn't assembled into a narrative an investor can price. Heritage reads as 'old', not 'proven'.",
    },
    tensionLine: {
      zh: "你有三十年的證據，卻用一句話把它變成了年紀。",
      en: "Thirty years of proof, written as thirty years of age.",
    },
  },
  {
    id: "science-comms",
    label: { zh: "永續產品的科學聲明", en: "A sustainability product's science claim" },
    input:
      "研究證實本產品成分天然無毒，對環境完全無害，是最安全的永續選擇，讓您安心守護家人與地球。",
    scores: { clarity: 62, differentiation: 33, evidence: 9 },
    findings: {
      zh: "「研究證實」「完全無害」「最安全」，三個絕對化聲明，沒有一個標注證據等級或研究來源。這不是科學傳播，是把複雜證據降維成安心話術；監管單位與有科學素養的買家，第一眼就會扣分。",
      en: "Three absolute claims, zero graded evidence. Flattening complex science into reassurance, the exact move regulators and literate buyers discount on sight.",
    },
    tensionLine: {
      zh: "你把證據說成了保證，而市場信任的是敢標注不確定性的人。",
      en: "You turned evidence into a guarantee. Markets trust the ones who label their uncertainty.",
    },
  },
]
